import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, UserPlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import InviktusLogo from "@/components/InviktusLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, formatAuthError } from "@/contexts/AuthContext";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    setLoading(true);
    try {
      await register({ email, password, name, company });
      setDone(true);
    } catch (err) {
      const msg = formatAuthError(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <div className="mx-auto flex min-h-screen max-w-7xl items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between">
            <Link to="/" className="inline-flex">
              <InviktusLogo size={42} tone="light" />
            </Link>
            <ThemeToggle />
          </div>

          <div className="mt-10 border border-white/15 bg-white/[0.04] p-8 backdrop-blur-xl"
               data-testid="register-card">
            {done ? (
              <div className="text-center" data-testid="register-success">
                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
                <h1 className="mt-4 font-display text-2xl text-white">Solicitud recibida</h1>
                <p className="mt-3 text-sm text-slate-300/90">
                  Su cuenta queda en estado <strong className="text-white">pendiente</strong>.
                  Un administrador de Inviktus la revisará; cuando esté aprobada podrá iniciar sesión
                  con su correo y contraseña. Si lo necesita, el equipo de Inviktus se pondrá en contacto con usted.
                </p>
                <Button
                  onClick={() => nav("/login")}
                  className="mt-6 rounded-sm bg-gradient-to-r from-accent-blue to-accent-purple text-[11px] uppercase tracking-[0.2em] text-white"
                  data-testid="goto-login-btn"
                >
                  Ir a iniciar sesión
                </Button>
              </div>
            ) : (
              <>
                <p className="eyebrow text-accent-blue">Nueva cuenta</p>
                <h1 className="mt-2 font-display text-3xl text-white">
                  Solicitar acceso
                </h1>
                <p className="mt-2 text-sm text-slate-300/80">
                  Complete sus datos. Un administrador aprobará el acceso
                  antes de su primer ingreso.
                </p>

                <form onSubmit={submit} className="mt-8 space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.18em] text-slate-300/80">Nombre</label>
                      <Input value={name} onChange={(e) => setName(e.target.value)}
                             className="mt-1 h-10 rounded-sm border-white/15 bg-white/5 text-white focus-visible:ring-accent-blue"
                             data-testid="register-name-input" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.18em] text-slate-300/80">Empresa</label>
                      <Input value={company} onChange={(e) => setCompany(e.target.value)}
                             className="mt-1 h-10 rounded-sm border-white/15 bg-white/5 text-white focus-visible:ring-accent-blue"
                             data-testid="register-company-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.18em] text-slate-300/80">Correo</label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                           className="mt-1 h-10 rounded-sm border-white/15 bg-white/5 text-white focus-visible:ring-accent-blue"
                           data-testid="register-email-input" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.18em] text-slate-300/80">
                      Contraseña (mínimo 8)
                    </label>
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                           className="mt-1 h-10 rounded-sm border-white/15 bg-white/5 text-white focus-visible:ring-accent-blue"
                           data-testid="register-password-input" />
                  </div>

                  {error ? (
                    <div className="rounded-sm border border-red-400/30 bg-red-500/10 p-3 text-[12px] text-red-200"
                         data-testid="register-error">
                      {error}
                    </div>
                  ) : null}

                  <Button type="submit" disabled={loading}
                          className="w-full rounded-sm bg-gradient-to-r from-accent-blue to-accent-purple text-[11px] uppercase tracking-[0.2em] text-white hover:opacity-95"
                          data-testid="register-submit-btn">
                    {loading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                             : <UserPlus className="mr-2 h-3.5 w-3.5" />}
                    Solicitar acceso
                  </Button>
                </form>

                <div className="mt-6 flex items-center justify-between text-[12px] text-slate-300/80">
                  <Link to="/" className="hover:text-white">← Volver al inicio</Link>
                  <Link to="/login" className="text-accent-blue hover:underline">
                    ¿Ya tiene cuenta? Inicie sesión
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
